import React from 'react';
import {
    KebabFilled,
    List,
    TrashOutlined,
    Divider,
    Group,
    DropdownMenu,
    ColorMode,
    DownloadOutlined,
    Text,
    IconButton,
    SharingOutlined,
    AddToCollectionOutlined,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import {
    CreatorDownloadSource,
    CreatorCollectionPlaybackMeta,
    Sample,
} from 'types';
import useShareCreatorButton from 'components/Creator/ShareCreator/useShareCreatorButton';
import { usePrivateActionClickHandler } from 'hooks/usePrivateActionClickHandler/usePrivateActionClickHandler';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import useAddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton/useAddSampleToCollectionButton';
import { useOpenAddToCollectionPrompt } from 'actions/UserCollection';
import useRemoveFromCreatorButton from 'components/Creator/RemoveFromCreatorButton/useRemoveFromCreatorButton';
import { getCollectionPlaceholder } from '../CreatorHeader.helpers';
import { useClickHandlers } from './CreatorHeaderMobileMenu.hooks';

export interface CreatorHeaderMobileMenuProps {
    samples: Sample[];
    creatorDownloadSource: CreatorDownloadSource;
    playbackMeta?: CreatorCollectionPlaybackMeta;
    isDisabled: boolean;
}

export interface ActionsGroupProps extends CreatorHeaderMobileMenuProps {
    hide: () => void;
}

const ActionsGroup = ({
    samples,
    creatorDownloadSource,
    playbackMeta,
    isDisabled,
    hide,
}: ActionsGroupProps): JSX.Element => {
    // CONTEXT
    const { dispatch, openShareModal } = useCreatorContext();
    const purchaseController = usePurchaseControllerContext();

    const {
        handleShareClick,
        handleDownloadFullTrackClick,
        handleDownloadSamplesClick,
        handleAddSamplesToCollectionClick,
    } = useClickHandlers({
        hide,
        openShareModal,
        purchaseController,
        creatorDownloadSource,
        dispatch,
    });

    const { anchorRef, openAddToCollection, AddToCollection } =
        useOpenAddToCollectionPrompt();

    // use buttons hooks
    const { handleClick: shareButtonClick } = useShareCreatorButton({
        onClick: handleShareClick,
    });

    const handleDownloadButtonClick = usePrivateActionClickHandler({
        authOnly: true,
        onClick: shareButtonClick,
        userAction: {
            type: '',
            description: t`share your track`,
            args: [],
        },
    });

    const placeholder = getCollectionPlaceholder(playbackMeta);

    const { handleOnClick: handleAddToCollectionClick } =
        useAddSampleToCollectionButton({
            onClick: handleAddSamplesToCollectionClick,
            samples: samples,
            source: { interaction: 'Creator' },
            hide,
            openAddToCollection,
            placeholder,
        });

    const { handleClick: handleRemoveFromCreatorClick } =
        useRemoveFromCreatorButton({ onClick: hide });

    const showDownloadActions = samples.length > 0;

    return (
        <List pt="md" pb="md">
            {showDownloadActions && (
                <>
                    <List.Item
                        as="button"
                        disabled={isDisabled}
                        onClick={handleDownloadFullTrackClick}
                        aria-label={t`Download full track`}
                        icon={DownloadOutlined}
                    >
                        <Group direction={'vertical'} mb="xs">
                            <Text mb="xxs">
                                <Trans>Download full track</Trans>
                            </Text>
                            <Text contrast="low" size="xs">
                                <Trans>Download your mixdown</Trans>
                            </Text>
                        </Group>
                    </List.Item>
                    <List.Item
                        as="button"
                        disabled={isDisabled}
                        onClick={handleDownloadSamplesClick}
                        aria-label={t`Download full track`}
                        icon={DownloadOutlined}
                    >
                        <Group direction={'vertical'} mb="xs">
                            <Text mb="xxs">
                                <Trans>Download samples</Trans>
                            </Text>
                            <Text contrast="low" size="xs">
                                <Trans>Your processed samples</Trans>
                            </Text>
                        </Group>
                    </List.Item>
                </>
            )}
            <Divider />
            <List.Item
                as="button"
                disabled={isDisabled}
                onClick={handleDownloadButtonClick}
                icon={SharingOutlined}
                aria-label={t`Share your track`}
            >
                <Trans>Share your track</Trans>
            </List.Item>
            <Divider />
            <>
                <List.Item
                    as="button"
                    onClick={handleAddToCollectionClick}
                    disabled={isDisabled}
                    icon={AddToCollectionOutlined}
                >
                    <Trans>Add to collection</Trans>
                </List.Item>
                <AddToCollection samples={samples} anchorRef={anchorRef} />
            </>
            <List.Item
                as="button"
                onClick={handleRemoveFromCreatorClick}
                icon={TrashOutlined}
            >
                <Trans>Delete</Trans>
            </List.Item>
        </List>
    );
};

export const CreatorHeaderMobileMenu = ({
    ...props
}: CreatorHeaderMobileMenuProps): JSX.Element => {
    return (
        <Group>
            <Group.Item>
                <ColorMode mode="dark">
                    <DropdownMenu py={0} px={0} placement="bottom-end">
                        {({ isOpen, close }): JSX.Element => (
                            <>
                                <DropdownMenu.Trigger>
                                    <IconButton
                                        data-component="CreatorHeaderMobileMenu"
                                        icon={KebabFilled}
                                        variant="secondary"
                                        isRounded
                                        aria-haspopup="true"
                                        aria-expanded={isOpen}
                                        aria-label={t`View more`}
                                        size="sm"
                                        label={t`More`}
                                    />
                                </DropdownMenu.Trigger>
                                <DropdownMenu.List>
                                    <ActionsGroup {...props} hide={close} />
                                </DropdownMenu.List>
                            </>
                        )}
                    </DropdownMenu>
                </ColorMode>
            </Group.Item>
        </Group>
    );
};
